import { type SyntheticEvent, useState } from "react";
import { useFormContext } from "react-hook-form";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Button } from "ui/button";
import { Box } from "ui/box";
import { Tab, Tabs } from "ui/tabs";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import toast from "components/partials/toast/toast";

import { publishBeeConfigs } from "hooks/api/use-bee-config";

import { useCurrentClient } from "hooks/api/use-clients";
import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";

import { BeeConfigExtended, BrandStyleData } from "types/bee";

import Icons from "assets";

interface PreviewToolbarProps {
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  tabHasError: (tab: string) => boolean;
}

const THEME_TABS = [
  { name: "Colors", path: "colors" },
  { name: "Fonts", path: "fonts" },
  { name: "Typography", path: "typography" },
  { name: "Button", path: "button" },
  { name: "Banner", path: "banner" },
  { name: "Layout", path: "layout" },
];

const TAB_LABEL_SX: SxProps<Theme> = { display: "inline-flex", alignItems: "center", gap: 1 };

/** Red in every tab state, the selected fill included: the tab's own colour is not inherited. */
const TAB_ERROR_SX: SxProps<Theme> = (theme) => ({ display: "inline-flex", color: theme.palette.error.main });

export const PreviewToolbar = ({ currentTab, setCurrentTab, tabHasError }: PreviewToolbarProps) => {
  //form
  const {
    formState: { isDirty, errors },
  } = useFormContext();

  const { beeConfigData, brandStyleData, isEditMode, setIsEditMode, setThemeShouldBeUpdated } =
    useThemePreviewContext();

  const client = useCurrentClient();

  const [showModal, setShowModal] = useState<boolean>(false);

  const handleConfirm = async () => {
    setShowModal(false);
    setIsEditMode(false);

    // reset theme context and form state on cancel
    setThemeShouldBeUpdated(true);
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowModal(true);
    } else {
      setIsEditMode(false);
    }
  };

  const anyErrOccured = ["colors", "fonts", "typography", "button", "banner", "layout"].some(
    (tabName) => tabName.toLowerCase() in errors,
  );

  const handleTabChange = (_event: SyntheticEvent, tabName: string) => {
    setCurrentTab(tabName);
  };

  const handlePublish = async () => {
    try {
      // Single atomic call that updates both configs together - ensures they stay in sync with no race condition
      await publishBeeConfigs(
        client.id,
        beeConfigData as BeeConfigExtended,
        brandStyleData as BrandStyleData,
      );
    } catch (error) {
      console.error("Failed to publish theme configs:", error);
      toast.error({
        data: {
          title: "Failed to publish theme",
        },
      });
    }
  };

  return (
    <div className="flex px-8 py-3 justify-between items-center">
      <Tabs
        size="large"
        aria-label="theme-preview-tabs-toggle"
        value={currentTab}
        onChange={handleTabChange}>
        {THEME_TABS.map(({ name, path }) => (
          <Tab
            key={path}
            value={name}
            label={
              <Box component="span" sx={TAB_LABEL_SX}>
                {name}
                {tabHasError(path) && (
                  <Box component="span" sx={TAB_ERROR_SX}>
                    <Icons.ExclamationCircle role="alert" />
                  </Box>
                )}
              </Box>
            }
          />
        ))}
      </Tabs>

      {isEditMode ? (
        <div className="flex gap-2">
          <Button variant="text" size="large" onClick={handleCancel}>
            Cancel
          </Button>
          <ConfirmationDialog
            open={showModal}
            onClose={() => setShowModal(false)}
            headline="Are you sure you want to cancel your changes?"
            message="All unsaved fields won't be saved. The form will be reset with the last saved values."
            cancelButtonText="Stay in edit mode"
            confirmButtonText="Leave edit mode"
            onConfirm={handleConfirm}
            intent="warning"
          />
          <Button type="submit" size="large" disabled={anyErrOccured}>
            Save
          </Button>
        </div>
      ) : (
        <div className="flex gap-3">
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              setIsEditMode(true);
            }}>
            Edit
          </Button>

          <Button size="large" disabled={!beeConfigData || !brandStyleData} onClick={handlePublish}>
            Publish
          </Button>
        </div>
      )}
    </div>
  );
};
